import type { SplitIds } from '@/clientOnlyUtils/clientUtils';
import { calculateNewSIds, idsIncluded, joinIds } from '@/clientOnlyUtils/clientUtils';
import { DateTime } from 'luxon';
import React from 'react';

import type { PoseidonAuthData } from '~/components/AuthedPage';
import { ContentDivider } from '@poseidon/components/ContentDivider';
import { ContentSection } from '@poseidon/components/ContentSection';
import { Button, ButtonLink, Tooltip } from '@poseidon/components/Input2';
import { PaddedScrollable } from '@poseidon/components/Scrollable';
import { Alerts, General, Maps, Time, Users } from '@poseidon/components/StrokeIcons';
import { Warnings } from '@poseidon/components/Warnings';

import type { NextPageWithLayout } from '~/pages/_app';
import PageHeader from '@poseidon/components/PageHeader';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import { api } from '@/utils/api';
import { LoadingContacts } from '@poseidon/components/LoadingContactCard';
import {
  cadenceOldToNewFormat,
  cadenceStepsOldToNewFormat,
  contactOldToNewFormat,
  openContactDetailQuery,
  orgOldToNewFormat,
} from '~/components/ContactCard/ContactCard';
import { ContactCardPure } from '~/components/ContactCard/ContactCardPure';
import { ContentViewedCard } from '~/components/ContactCard/ContentViewedCard';
import { DemoContactCards } from '~/components/ContactCard/DemoContactCard';
import { ResponseCard } from '~/components/ContactCard/ResponseCard';
import { ContactOrgDetailPage } from '~/components/ContactOrgDetailPage';
import Filters from '~/components/Filters2/Filters';
import { useOnboardingContext } from '~/components/Onboarding/onboarding.context';
import { dataOnboardingIds } from '~/components/Onboarding/onboarding.model';
import { ContactSelectedPopup } from '~/components/SelectedPopup/ContactSelectedPopup';
import { SEO } from '~/components/Seo';
import { useContactSetStageMutation } from '~/hooks/trpc/useContactMutation';

const defaultOrderByDirection = 'desc';
const defaultLimit = 3;
const to = DateTime.now().endOf('day').toMillis();

const Leads: NextPageWithLayout<{
  userTeam: PoseidonAuthData;
}> = ({ userTeam }) => {
  const [{ shouldShowChecklist }, { resetOnboarding }] = useOnboardingContext();
  const allEmailCredentials = api.team.emailCredentials.useQuery({ teamId: userTeam.teamId });

  return (
    <>
      <SEO title="Take Action" />
      <PageHeader icon={<Maps.Rocket />} title="Take Action">
        <ContentDivider
          align="right"
          end={
            <>
              {!shouldShowChecklist && (
                <Tooltip tooltip="Show Tour Menu" tooltipPosition="top">
                  <div
                    onClick={resetOnboarding}
                    className="cursor-pointer rounded-full bg-zumthor-900 text-zumthor-50 hover:bg-zumthor-700">
                    <General.HelpCircle />
                  </div>
                </Tooltip>
              )}
            </>
          }
        />
      </PageHeader>

      <Filters
        teamId={userTeam.teamId}
        teamName={userTeam.teamName}
        userId={userTeam.userId}
        teamLogo={userTeam.teamLogo}
        show={['userId']}
      />

      <PaddedScrollable>
        <Warnings
          userId={userTeam.userId}
          userTeamTimezoneName={userTeam.userTeamTimezoneName}
          warnings={userTeam.warnings}
          emailCredentials={allEmailCredentials.data?.emailCredentials}
          isEmailCredentialsLoading={allEmailCredentials.isPending}
        />
        <ResponsesSection userTeam={userTeam} />
        <ContentViewedSection userTeam={userTeam} />
        <GlobalRemindersSection userTeam={userTeam} />
        {userTeam.isSuperAdmin && <ErrorsSection userTeam={userTeam} />}
      </PaddedScrollable>
    </>
  );
};

Leads.layout = 'withLeftNav';
Leads.createWAuth = (C, pageProps, authData) => <C userTeam={authData} {...pageProps} />;
Leads.getDetailPage = (authData) => (
  <ContactOrgDetailPage
    userTeam={authData}
    authTeamId={authData.teamId}
    authUserId={authData.userId}
    authIsSuperAdmin={authData.isSuperAdmin}
  />
);
Leads.getSelectedPopup = (authData) => (
  <ContactSelectedPopup
    teamId={authData.teamId}
    userId={authData.userId}
    isPremium={authData.isPremium}
    creditsRemaining={authData.creditsRemaining}
  />
);

export default Leads;

const ResponsesSection = ({ userTeam }: { userTeam: PoseidonAuthData }) => {
  const teamId = userTeam.teamId;
  const [query, setQuery] = useRouterQuery();
  const cadencesQuery = api.contact.responded.useQuery({
    pagination: {
      offset: query.offset,
      limit: defaultLimit,
    },
    teamId,
    userId: query.userId,
    templateId: query.templateId,
    orderByDirection: query.order ?? defaultOrderByDirection,
  });

  const [{ fakeTakeAction }] = useOnboardingContext();

  const idsOnPage =
    cadencesQuery.data?.responses?.map((d) =>
      joinIds({ contactId: d.contact.id, cadenceId: d.cadence?.id, interactionId: d.response.id }),
    ) || [];

  const onToggleChecked = (data: SplitIds, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setQuery((q) => ({
      ...q,
      selectedIds: calculateNewSIds(joinIds(data), query.selectedIds, idsOnPage, e),
    }));
  };

  return (
    <ContentSection size="small" data-onboarding={dataOnboardingIds.takeActionContacts}>
      <ContentDivider icon={<Users.UsersLeft />}>Respond</ContentDivider>
      {fakeTakeAction && <DemoContactCards />}
      {!fakeTakeAction && cadencesQuery.isLoading && <LoadingContacts onToggleChecked />}
      {!fakeTakeAction &&
        cadencesQuery.data?.responses.map((data) => (
          <ResponseCard
            key={data.response.id}
            contact={data.contact}
            org={data.org}
            contactUser={data.interactionUser}
            authedUser={data.authedUser}
            response={data.response}
            onToggleChecked={onToggleChecked}
            cadence={data.cadence}
          />
        ))}

      <div className="flex justify-end">
        {fakeTakeAction && <Button>See all 10 responses</Button>}
        {!fakeTakeAction && (
          <ButtonLink
            size="xs"
            href={{
              pathname: '/leads/responses',
              query: { ...query },
            }}>
            See all responses
          </ButtonLink>
        )}
      </div>
    </ContentSection>
  );
};

const ContentViewedSection = ({ userTeam }: { userTeam: PoseidonAuthData }) => {
  const teamId = userTeam.teamId;
  const [query, setQuery] = useRouterQuery();
  const contentViewedQuery = api.contact.contentViewed.useQuery({
    pagination: {
      offset: query.offset,
      limit: defaultLimit,
    },
    teamId,
    userId: query.userId,
    templateId: query.templateId,
    orderByDirection: query.order ?? defaultOrderByDirection,
  });

  const idsOnPage =
    contentViewedQuery.data?.views?.map((d) => joinIds({ contactId: d.contact.id, interactionId: d.interaction.id })) ||
    [];

  const onToggleChecked = (data: SplitIds, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setQuery((q) => ({
      ...q,
      selectedIds: calculateNewSIds(joinIds(data), query.selectedIds, idsOnPage, e),
    }));
  };

  return (
    <ContentSection size="small">
      <ContentDivider icon={<General.Eye />}>Content Viewed</ContentDivider>
      {contentViewedQuery.isLoading && <LoadingContacts onToggleChecked />}
      {contentViewedQuery.data?.views.map((data) => (
        <ContentViewedCard
          key={data.interaction.id}
          contact={contactOldToNewFormat(data.contact)}
          org={orgOldToNewFormat(data.org)}
          contactUser={data.interactionUser}
          authedUserLiConnected={data.authedUser?.liConnected}
          authedUserLiConnectedAt={data.authedUser?.liConnectedAt}
          interactionId={data.interaction.id}
          documentTitle={data.documentView.title}
          onToggleChecked={onToggleChecked}
        />
      ))}
      <div className="flex justify-end">
        <ButtonLink
          size="xs"
          href={{
            pathname: '/leads/contentviewed',
            query: { ...query },
          }}>
          See all content views
        </ButtonLink>
      </div>
    </ContentSection>
  );
};

const GlobalRemindersSection = ({ userTeam }: { userTeam: PoseidonAuthData }) => {
  const teamId = userTeam.teamId;
  const [query, setQuery] = useRouterQuery();
  const contactSetStageMutation = useContactSetStageMutation();
  const stagesQuery = api.stage.all.useQuery({ teamId });

  const remindersQuery = api.globalReminder.paginated.useQuery({
    pagination: {
      offset: query.offset,
      limit: defaultLimit,
    },
    teamId,
    userId: query.userId,
    to,
    orderByDirection: query.order ?? defaultOrderByDirection,
  });

  const idsOnPage =
    remindersQuery.data?.globalReminders?.map((d) =>
      joinIds({ contactId: d.contact.id, cadenceId: d.cadence?.id, reminderId: d.globalReminder.id }),
    ) || [];

  const onToggleChecked = (data: SplitIds, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setQuery((q) => ({
      ...q,
      selectedIds: calculateNewSIds(joinIds(data), query.selectedIds, idsOnPage, e),
    }));
  };

  return (
    <ContentSection size="small">
      <ContentDivider icon={<Time.AlarmClock />}>Reminders</ContentDivider>
      {remindersQuery.isLoading && <LoadingContacts onToggleChecked />}
      {remindersQuery.data?.globalReminders.map((data, index) => {
        const cadenceUser = data.cadenceContactUser;
        const reminderUser = data.reminderContactUser;

        return (
          <ContactCardPure
            index={index + 1}
            key={data.globalReminder.id}
            contact={contactOldToNewFormat(data.contact)}
            contactUser={cadenceUser ?? reminderUser}
            org={orgOldToNewFormat(data.contactOrg)}
            cadence={cadenceOldToNewFormat(data.cadence, cadenceUser ?? reminderUser)}
            cadenceSteps={cadenceStepsOldToNewFormat(data.cadenceSteps)}
            firstReminder={data.globalReminder ?? null}
            canNotRunStepTypes={undefined}
            onToggleChecked={onToggleChecked}
            stageUpdating={
              contactSetStageMutation.isPending && contactSetStageMutation.variables?.contactId === data.contact.id
            }
            onStageChange={(stageId) => contactSetStageMutation.mutate({ contactId: data.contact.id, stageId })}
            onClick={() =>
              setQuery((q) => openContactDetailQuery(q, { contactId: data.contact.id, cadenceId: data.cadence?.id }))
            }
            isActive={query.id === joinIds({ contactId: data.contact.id, cadenceId: data.cadence?.id })}
            isChecked={idsIncluded({ contactId: data.contact.id, cadenceId: data.cadence?.id }, query.selectedIds)}
            stages={stagesQuery.data?.stages ?? []}
            stagesLoading={stagesQuery.isPending}
            show={'reminder'}
          />
        );
      })}
      <div className="flex justify-end">
        <ButtonLink
          size="xs"
          href={{
            pathname: '/leads/reminders',
            query: { ...query },
          }}>
          See all reminders
        </ButtonLink>
      </div>
    </ContentSection>
  );
};

const ErrorsSection = ({ userTeam }: { userTeam: PoseidonAuthData }) => {
  const [query, setQuery] = useRouterQuery();
  const teamId = userTeam.teamId;
  const contactSetStageMutation = useContactSetStageMutation();
  const stagesQuery = api.stage.all.useQuery({ teamId });

  const contactsQuery = api.contact.interrupted.useQuery({
    teamId,
    userId: query.userId,
    pagination: {
      limit: defaultLimit,
    },
  });

  const idsOnPage =
    contactsQuery.data?.contacts?.map((d) =>
      joinIds({ contactId: d.contact.id, cadenceId: d.activeCadence?.cadence.id }),
    ) || [];

  const onToggleChecked = (data: SplitIds, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setQuery((q) => ({
      ...q,
      selectedIds: calculateNewSIds(joinIds(data), query.selectedIds, idsOnPage, e),
    }));
  };

  return (
    <ContentSection size="small">
      <ContentDivider icon={<Alerts.AlertTriangle />}>Interrupted</ContentDivider>
      {contactsQuery.isLoading && <LoadingContacts onToggleChecked />}
      {contactsQuery.data?.contacts.map((data, index) => (
        <ContactCardPure
          index={index + 1}
          key={data.contact.id}
          contact={contactOldToNewFormat(data.contact)}
          contactUser={data.activeCadence?.cadenceUser ?? data.authedUser}
          org={orgOldToNewFormat(data.org)}
          cadence={cadenceOldToNewFormat(
            data.activeCadence?.cadence,
            data.activeCadence?.cadenceUser ?? data.authedUser,
          )}
          cadenceSteps={cadenceStepsOldToNewFormat(data.activeCadence?.runnableSteps)}
          canNotRunStepTypes={undefined}
          onToggleChecked={onToggleChecked}
          stageUpdating={
            contactSetStageMutation.isPending && contactSetStageMutation.variables?.contactId === data.contact.id
          }
          onStageChange={(stageId) => contactSetStageMutation.mutate({ contactId: data.contact.id, stageId })}
          onClick={() =>
            setQuery((q) =>
              openContactDetailQuery(q, { contactId: data.contact.id, cadenceId: data.activeCadence?.cadence.id }),
            )
          }
          isActive={query.id === joinIds({ contactId: data.contact.id, cadenceId: data.activeCadence?.cadence.id })}
          isChecked={idsIncluded(
            { contactId: data.contact.id, cadenceId: data.activeCadence?.cadence.id },
            query.selectedIds,
          )}
          stages={stagesQuery.data?.stages ?? []}
          stagesLoading={stagesQuery.isPending}
        />
      ))}
      <div className="flex justify-end">
        <ButtonLink
          size="xs"
          href={{
            pathname: '/leads/interrupted',
            query: { ...query },
          }}>
          See all interruptions
        </ButtonLink>
      </div>
    </ContentSection>
  );
};
